import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Dimensions,
  Keyboard,
  Linking,
  Platform,
} from 'react-native';
import SystemNavigationBar from 'react-native-system-navigation-bar';
import { useNavigation } from '@react-navigation/native';
import { showConfirm, showAlert } from '@/utils/common/alert';
import { KeyboardDismissWrapper } from '@/components/common/KeyboardDismissWrapper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { ResioLogo } from '@/assets/ResioLogo';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { 
  RESIDENT_NAV_COLORS,
  RESIDENT_PATH_TO_SCREEN,
  IResidentUserState,
} from '@/constants/residents/residentNavigation';
import { SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { ResidentNavList } from './ResidentNavList';
import { useAppSelector } from '@/store';
import { MobileNotificationsModal } from '@/components/residents/notification/mobile/MobileNotificationsModal';
import { useUnreadNotificationsCount } from '@/hooks/common/useUnreadNotificationsCount';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

interface ResidentMobileHeaderWithMenuProps {
  onLogoPress?: () => void;
  onSignOut?: () => void;
  onNavigate?: (path: string) => void;
  onNotificationPress?: () => void;
  currentPath?: string;
  currentTitle?: string;
  showNotification?: boolean;
}

export const ResidentMobileHeaderWithMenu: React.FC<ResidentMobileHeaderWithMenuProps> = ({
  onLogoPress,
  onSignOut,
  onNavigate,
  onNotificationPress,
  currentPath = '/payments',
  currentTitle = 'Payments',
  showNotification = true,
}) => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<any>();
  const { user } = useAppSelector((state) => state.auth);
  
  // Use centralized notification count hook - includes bulletins + marketplace feedback requests
  const { total: unreadCount } = useUnreadNotificationsCount();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState(false);
  const [animationError, setAnimationError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  
  // Build user state for navigation conditions
  const userState: IResidentUserState = {
    customerType: user?.customerType,
    isWifiEnabled: false, // TODO: Get from property settings
  };
  
  // Calculate total header height including safe area
  const totalHeaderHeight = insets.top + 74;
  
  // Animation values - menu starts below header + safe area
  const slideAnim = useRef(new Animated.Value(-(SCREEN_HEIGHT - totalHeaderHeight))).current;
  const overlayOpacity = useRef(new Animated.Value(0)).current;

  // Cleanup animations on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      slideAnim.removeAllListeners();
      overlayOpacity.removeAllListeners();
    };
  }, [slideAnim, overlayOpacity]);

  const handleAnimationError = useCallback((error: string) => {
    console.error('Animation error:', error);
    setAnimationError(error);
    
    // Reset menu state on animation failure
    setIsMenuOpen(false);
    slideAnim.setValue(-(SCREEN_HEIGHT - totalHeaderHeight));
    overlayOpacity.setValue(0);
  }, [slideAnim, overlayOpacity, totalHeaderHeight]);

  // Close menu with animation and wait for completion
  const closeMenuWithAnimation = useCallback((): Promise<void> => {
    if (Platform.OS === 'android') {
      SystemNavigationBar.setNavigationColor(SEMANTIC_COLORS.background.primary);
    }
    return new Promise((resolve) => {
      try {
        Animated.parallel([
          Animated.timing(slideAnim, {
            toValue: -(SCREEN_HEIGHT - totalHeaderHeight),
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.timing(overlayOpacity, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
        ]).start(({ finished }) => {
          if (isMountedRef.current) {
            setAnimationError(null);
          }
          resolve();
        });
      } catch (error) {
        console.error('Error closing menu animation:', error);
        if (isMountedRef.current) {
          setIsMenuOpen(false);
        }
        resolve();
      }
    });
  }, [slideAnim, overlayOpacity, totalHeaderHeight]);

  const toggleMenu = useCallback(() => {
    try {
      // Dismiss keyboard when menu is toggled
      Keyboard.dismiss();
      
      if (isMenuOpen) {
        // Close menu - slide up and fade out
        if (Platform.OS === 'android') {
          SystemNavigationBar.setNavigationColor(SEMANTIC_COLORS.background.primary);
        }
        Animated.parallel([
          Animated.timing(slideAnim, {
            toValue: -(SCREEN_HEIGHT - totalHeaderHeight),
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.timing(overlayOpacity, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
        ]).start(({ finished }) => {
          if (isMountedRef.current) {
            setIsMenuOpen(false);
            setAnimationError(null);
          }
        });
      } else {
        // Open menu - slide down and fade in
        if (Platform.OS === 'android') {
          SystemNavigationBar.setNavigationColor(RESIDENT_NAV_COLORS.BACKGROUND);
        }
        setIsMenuOpen(true);
        Animated.parallel([
          Animated.timing(slideAnim, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.timing(overlayOpacity, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
          }),
        ]).start(({ finished }) => {
          if (isMountedRef.current) {
            setAnimationError(null);
          }
        });
      }
    } catch (error) {
      handleAnimationError(`Animation failed: ${error}`);
    }
  }, [isMenuOpen, slideAnim, overlayOpacity, totalHeaderHeight, handleAnimationError]);

  const handleSignOut = useCallback(async () => {
    const confirmed = await showConfirm(
      'Sign Out',
      'Are you sure you want to sign out?',
      'Sign Out',
      'Cancel'
    );
    
    if (!confirmed) return;
    
    // Close menu with animation and wait for completion
    await closeMenuWithAnimation();
    // Now call the logout function
    onSignOut?.();
  }, [closeMenuWithAnimation, onSignOut]);

  const handleNavigation = useCallback((path: string) => {
    try {
      // Immediately reset menu state
      setIsMenuOpen(false);
      
      // Navigate immediately for seamless transition
      onNavigate?.(path);
      
      // Close menu animation concurrently
      closeMenuWithAnimation().catch((error) => {
        console.error('Menu close animation error:', error);
      });
    } catch (error) {
      console.error('Navigation error:', error);
      showAlert('Navigation Error', 'Failed to navigate. Please try again.');
    }
  }, [closeMenuWithAnimation, onNavigate]);

  // Open notification modal when bell is pressed
  const handleNotificationPress = useCallback(() => {
    setIsNotificationModalVisible(true);
  }, []);

  // Handle "See all notifications" from modal - navigates to full notification screen
  const handleSeeAllNotifications = useCallback(() => {
    setIsNotificationModalVisible(false);
    onNotificationPress?.();
  }, [onNotificationPress]);

  // Close notification modal
  const handleCloseNotificationModal = useCallback(() => {
    setIsNotificationModalVisible(false);
  }, []);

  // Handle notification settings - navigate to account settings with notification settings section
  const handleNotificationSettings = useCallback(() => {
    setIsNotificationModalVisible(false);
    try {
      navigation.navigate('MobileAccountSettings', { section: 'notificationSettings' });
    } catch (error) {
      console.warn('Failed to navigate to notification settings:', error);
    }
  }, [navigation]);

  const handleResioLinkPress = useCallback(() => {
    Linking.openURL('https://resio.com');
  }, []);

  // Show error state if animation fails
  if (animationError) {
    return (
      <View style={[styles.safeArea, { paddingTop: insets.top }]}>
        <View style={styles.header}>
          <Text style={styles.errorText}>Menu Error: {animationError}</Text>
          <TouchableOpacity
            onPress={() => setAnimationError(null)}
            style={styles.retryButton}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <>
      {/* Fixed Header */}
      <View style={[styles.safeArea, { paddingTop: insets.top }]}>
        <KeyboardDismissWrapper style={styles.header}>
          {/* Left Side - Resio Logo + Notification */}
          <View style={styles.leftContainer}>
            <TouchableOpacity 
              style={styles.logoContainer}
              onPress={onLogoPress}
              activeOpacity={0.7}
              accessibilityLabel="Resio logo"
              accessibilityHint="Tap to go to home screen"
              accessibilityRole="button"
            >
              <ResioLogo width={25.5} height={34} />
              <View style={styles.resioNameContainer}>
                <Text style={styles.resioName}>RESIO</Text>
              </View>
            </TouchableOpacity>
            
            {showNotification && (
              <TouchableOpacity
                style={styles.notificationButton}
                onPress={handleNotificationPress}
                accessibilityLabel="Notifications"
                accessibilityRole="button"
              >
                <RESIDENTS_SVG_COMPONENTS.BELL 
                  width={20} 
                  height={20} 
                  color={RESIDENT_NAV_COLORS.TEXT_PRIMARY} 
                />
                {unreadCount > 0 && <View style={styles.unreadDot} />}
              </TouchableOpacity>
            )}
          </View>

          {/* Right Side - Title + Menu Toggle */}
          <TouchableOpacity 
            style={styles.menuContainer}
            onPress={toggleMenu}
            activeOpacity={0.7}
            accessibilityLabel={isMenuOpen ? "Close menu" : "Open menu"}
            accessibilityHint={isMenuOpen ? "Tap to close navigation menu" : "Tap to open navigation menu"}
            accessibilityRole="button"
            accessibilityState={{ expanded: isMenuOpen }}
          >
            {!isMenuOpen && <Text style={styles.menuText}>{currentTitle}</Text>}
            {isMenuOpen ? (
              <SVG_COMPONENTS.CANCEL_X {...SVG_DIMENSIONS.CANCEL_X} />
            ) : (
              <SVG_COMPONENTS.HAMBURGER {...SVG_DIMENSIONS.HAMBURGER} />
            )}
          </TouchableOpacity>
        </KeyboardDismissWrapper>
      </View>

      {/* Menu Overlay */}
      {isMenuOpen && (
        <Animated.View 
          style={[
            styles.menuOverlay,
            {
              top: totalHeaderHeight,
              opacity: overlayOpacity,
              transform: [{ translateY: slideAnim }],
            }
          ]}
          accessibilityViewIsModal={true}
        >
          {/* Navigation Menu */}
          <View style={styles.navMenu}>
            {/* Nav List - Scrollable */}
            <View style={styles.navListContainer}>
              <ResidentNavList
                currentPath={currentPath}
                onNavigate={handleNavigation}
                userState={userState}
              />
            </View>

            {/* Bottom Section - Fixed at bottom */}
            <View style={[styles.bottomSection, { paddingBottom: insets.bottom }]}>
              <Text style={styles.learnMoreText}>
                Learn more about us at{' '}
                <Text style={styles.resioLink} onPress={handleResioLinkPress}>
                  RESIO.COM
                </Text>
              </Text>
              
              <TouchableOpacity
                style={styles.signOutButton}
                onPress={handleSignOut}
                activeOpacity={0.8}
                accessibilityLabel="Sign out"
                accessibilityHint="Tap to sign out of your account"
                accessibilityRole="button"
              >
                <Text style={styles.signOutText} numberOfLines={1}>Sign out</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Animated.View>
      )}

      {/* Notification Modal */}
      <MobileNotificationsModal
        visible={isNotificationModalVisible}
        onClose={handleCloseNotificationModal}
        onSeeAll={handleSeeAllNotifications}
        onSettings={handleNotificationSettings}
        onPropertyChatPress={() => {
          setIsNotificationModalVisible(false);
          try { navigation.navigate('ResidentMessages'); } catch {}
        }}
      />
    </>
  );
};

const styles = StyleSheet.create({
  // Header styles - Following Figma specs exactly
  safeArea: {
    backgroundColor: RESIDENT_NAV_COLORS.BACKGROUND,
    zIndex: 1000,
  },
  header: {
    width: '100%',
    height: 74,
    backgroundColor: RESIDENT_NAV_COLORS.BACKGROUND,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
  },
  leftContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  resioNameContainer: {
    flexDirection: 'column',
    gap: 10,
  },
  resioName: {
    fontSize: 17,
    fontWeight: '600',
    color: RESIDENT_NAV_COLORS.TEXT_PRIMARY,
    letterSpacing: 0.5,
  },
  notificationButton: {
    width: 34,
    height: 34,
    backgroundColor: RESIDENT_NAV_COLORS.BORDER_PRIMARY,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unreadDot: {
    position: 'absolute',
    top: 7,
    right: 7,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: RESIDENT_NAV_COLORS.NOTIFICATION_KNOB,
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
  },
  menuText: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'right',
    color: RESIDENT_NAV_COLORS.TEXT_PRIMARY,
  },
  
  // Error handling styles
  errorText: {
    color: '#FF6B6B',
    fontSize: 12,
    flex: 1,
  },
  retryButton: {
    backgroundColor: RESIDENT_NAV_COLORS.TEXT_SECONDARY,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  retryButtonText: {
    color: RESIDENT_NAV_COLORS.TEXT_PRIMARY,
    fontSize: 12,
    fontWeight: '600',
  },
  
  // Menu overlay styles - positioned below header + safe area
  menuOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: RESIDENT_NAV_COLORS.BACKGROUND,
    zIndex: 999,
    flexDirection: 'column',
  },
  
  // Navigation menu styles - Following Figma specs exactly
  navMenu: {
    width: '100%',
    flex: 1,
    backgroundColor: RESIDENT_NAV_COLORS.BACKGROUND,
    paddingTop: 16,
    paddingHorizontal: 20,
    paddingBottom: 16,
    justifyContent: 'space-between',
  },
  navListContainer: {
    flex: 1,
  },
  
  // Bottom section styles
  bottomSection: {
    width: '100%',
    gap: 16,
    paddingTop: 16,
  },
  learnMoreText: {
    fontFamily: 'System',
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 21,
    color: '#F9EFFA', // purple/50 from Figma
  },
  resioLink: {
    fontFamily: 'System',
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 21,
    color: RESIDENT_NAV_COLORS.TEXT_SECONDARY,
  },
  signOutButton: {
    minWidth: 86,
    height: 40,
    backgroundColor: RESIDENT_NAV_COLORS.BUTTON_BACKGROUND,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  signOutText: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    color: RESIDENT_NAV_COLORS.BUTTON_TEXT,
    textAlign: 'center',
  },
});

